import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit, Trash2, X, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { useToast } from './Toast';
import { 
  Employee, 
  Location, 
  Position, 
  Schedule, 
  PayRecord, 
  PaymentType, 
  PaymentFrequency, 
  AssignLaborCostByOption, 
  WorkingDay,
  CreatePayRecordRequest,
  UpdatePayRecordRequest,
  PayRateMatrix,
  CreatePayRateMatrixRequest,
  UpdateEmployeeRequest,
} from '../types';
import { 
  payRecordApi, 
  paymentTypeApi, 
  paymentFrequencyApi, 
  assignLaborCostByOptionApi, 
  workingDayApi,
  companySettingsApi,
  nurseBandingApi,
  type NurseBandResolvedRates,
} from '../services/api';
import { CompanySettings } from '../types';
import { getPayRateBaseFieldLabel, formatPayListAmount } from '../utils/payRecordLabels';
import { usePermissions } from '../hooks/usePermissions';
import NurseBandYearHistoryPanel from './NurseBandYearHistoryPanel';
import { formatCompanyDate } from '../utils/companyDateFormat';
import NurseBandingResolvedRatesPanel from './NurseBandingResolvedRatesPanel';

function getPayMatrixRowKey(row: CreatePayRateMatrixRequest): string {
  const locationId = row.locationId != null && row.locationId > 0 ? row.locationId : '';
  const positionId = row.positionId != null && row.positionId > 0 ? row.positionId : '';
  const scheduleId = row.scheduleId != null && row.scheduleId > 0 ? row.scheduleId : '';
  return `${locationId}|${positionId}|${scheduleId}`;
}

function renumberPayRateMatrices(rows: CreatePayRateMatrixRequest[]): CreatePayRateMatrixRequest[] {
  return rows.map((row, index) => ({
    ...row,
    displayOrder: index,
  }));
}

function validatePayRateMatrices(rows: CreatePayRateMatrixRequest[]): string | null {
  const keys = new Map<string, number>();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 1;
    if (!row.positionId && !row.scheduleId) {
      return `Pay matrix row ${rowNum}: choose a Position or Schedule.`;
    }
    const rate = row.payRate;
    if (rate == null || !Number.isFinite(rate) || rate <= 0) {
      return `Pay matrix row ${rowNum}: enter a pay rate greater than zero.`;
    }
    const key = getPayMatrixRowKey(row);
    if (keys.has(key)) {
      const firstRow = keys.get(key)! + 1;
      return `Pay matrix row ${rowNum} uses the same position and schedule as row ${firstRow}. Each combination must be unique.`;
    }
    keys.set(key, i);
  }
  return null;
}

export type PayrollFormFields = Pick<
  UpdateEmployeeRequest,
  | 'worksNumber'
  | 'ppsNumber'
  | 'ban'
  | 'bic'
  | 'accountNumber'
  | 'sortCode'
  | 'accountName'
  | 'bankName'
  | 'isPremiumPayEligible'
  | 'allowAdditiveHourlyPay'
>;

interface PayrollAndPayRecordsProps {
  employee: Employee;
  payRecords: PayRecord[];
  locations: Location[];
  positions: Position[];
  schedules: Schedule[];
  onRefresh: () => void | Promise<void>;
  /** Bound to the main employee form (same as Work tab); omit for read-only legacy use */
  payrollForm?: PayrollFormFields;
  onPayrollFieldChange?: (field: keyof UpdateEmployeeRequest, value: unknown) => void;
}

const PayrollAndPayRecords: React.FC<PayrollAndPayRecordsProps> = ({
  employee,
  payRecords,
  locations,
  positions,
  schedules,
  onRefresh,
  payrollForm,
  onPayrollFieldChange,
}) => {
  const toast = useToast();
  const { healthcareNurseBandingPayrollEnabled } = usePermissions();
  const [showPayRecordModal, setShowPayRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PayRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationDialogMessage, setValidationDialogMessage] = useState<string | null>(null);
  
  // Lookup data
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);
  const [assignLaborCostByOptions, setAssignLaborCostByOptions] = useState<AssignLaborCostByOption[]>([]);
  const [workingDays, setWorkingDays] = useState<WorkingDay[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [hasNurseBandHistory, setHasNurseBandHistory] = useState(false);
  const [nurseBandingResolvedRates, setNurseBandingResolvedRates] = useState<NurseBandResolvedRates | null>(null);
  const nurseBandingPay = healthcareNurseBandingPayrollEnabled && hasNurseBandHistory;
  
  // Pay Record form data
  const [formData, setFormData] = useState<CreatePayRecordRequest>({
    employeeId: employee.id,
    locationId: employee.profile?.mainLocationId || 0,
    positionId: employee.profile?.mainPositionId || 0,
    scheduleId: 0,
    paymentTypeId: 0, // Will be set when payment types are loaded
    contractedHours: 0,
    baseRate: 0,
    overtimeEligible: false,
    isActive: false,
  });
  
  const [selectedWorkingDayIds, setSelectedWorkingDayIds] = useState<number[]>([]);
  const [payRateMatrices, setPayRateMatrices] = useState<CreatePayRateMatrixRequest[]>([]);

  // Get employee's main and sub locations/positions
  const employeeLocations = [
    ...(employee.profile?.mainLocationId ? [locations.find(l => l.id === employee.profile!.mainLocationId!)] : []),
    ...(employee.profile?.subLocationIds?.map(id => locations.find(l => l.id === id)) || [])
  ].filter(Boolean) as Location[];

  const employeePositions = [
    ...(employee.profile?.mainPositionId ? [positions.find(p => p.id === employee.profile!.mainPositionId!)] : []),
    ...(employee.profile?.subPositionIds?.map(id => positions.find(p => p.id === id)) || [])
  ].filter(Boolean) as Position[];

  const employeeSchedules = (employee.profile?.scheduleIds?.map(id => schedules.find(s => s.id === id)) || [])
    .filter(Boolean) as Schedule[];

  const payRecordModalSchedules = useMemo(() => {
    const byId = new Map<number, Schedule>();
    for (const schedule of employeeSchedules) {
      byId.set(schedule.id, schedule);
    }
    for (const row of payRateMatrices) {
      if (row.scheduleId && !byId.has(row.scheduleId)) {
        const schedule = schedules.find((s) => s.id === row.scheduleId);
        if (schedule) {
          byId.set(schedule.id, schedule);
        }
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [employeeSchedules, payRateMatrices, schedules]);

  useEffect(() => {
    const loadLookupData = async () => {
      try {
        const [types, frequencies, alcboOptions, wDays, companySettingsData] = await Promise.all([
          paymentTypeApi.getAll(),
          paymentFrequencyApi.getAll(),
          assignLaborCostByOptionApi.getAll(),
          workingDayApi.getAll(),
          companySettingsApi.get().catch(() => null)
        ]);
        setPaymentTypes(types);
        setPaymentFrequencies(frequencies);
        setAssignLaborCostByOptions(alcboOptions);
        setWorkingDays(wDays);
        setCompanySettings(companySettingsData);
        
        // Set default payment type to first available (or Hourly if exists)
        if (types.length > 0 && formData.paymentTypeId === 0) {
          const hourlyType = types.find(pt => pt.name === 'Hourly');
          const defaultType = hourlyType || types[0];
          setFormData(prev => ({ ...prev, paymentTypeId: defaultType.id }));
        }
      } catch (error) {
        console.error('Error loading lookup data:', error);
      }
    };
    loadLookupData();
  }, []);

  // Set default payment frequency based on payment type
  useEffect(() => {
    if (formData.paymentTypeId) {
      const paymentType = paymentTypes.find(pt => pt.id === formData.paymentTypeId);
      if (paymentType) {
        // For Hourly and Daily, default to Weekly frequency
        if (paymentType.name === 'Hourly' || paymentType.name === 'Daily') {
          const weeklyFreq = paymentFrequencies.find(pf => pf.name === 'Weekly');
          if (weeklyFreq) {
            setFormData({ ...formData, paymentFrequencyId: weeklyFreq.id });
          }
        } 
        // For Salary, default to Monthly frequency
        else if (paymentType.name === 'Salary') {
          const monthlyFreq = paymentFrequencies.find(pf => pf.name === 'Monthly');
          if (monthlyFreq) {
            setFormData({ ...formData, paymentFrequencyId: monthlyFreq.id });
          }
        }
        // Project-based payments default to "Contract" if available
        else if (paymentType.name === 'Project') {
          const contractFreq = paymentFrequencies.find(pf => pf.name === 'Contract');
          const monthlyInvoiceFreq = paymentFrequencies.find(pf => pf.name === 'Monthly Invoice');
          // If current frequency is not a Project-specific frequency, reset to Contract
          const currentFreq = paymentFrequencies.find(pf => pf.id === formData.paymentFrequencyId);
          if (currentFreq && currentFreq.name !== 'Contract' && currentFreq.name !== 'Monthly Invoice') {
            if (contractFreq) {
              setFormData({ ...formData, paymentFrequencyId: contractFreq.id });
            }
          } else if (!formData.paymentFrequencyId && contractFreq) {
            setFormData({ ...formData, paymentFrequencyId: contractFreq.id });
          }
        } else {
          // When switching away from Project, if current frequency is Project-specific, clear it
          const currentFreq = paymentFrequencies.find(pf => pf.id === formData.paymentFrequencyId);
          if (currentFreq && (currentFreq.name === 'Contract' || currentFreq.name === 'Monthly Invoice')) {
            setFormData({ ...formData, paymentFrequencyId: undefined });
          }
        }
      }
    }
  }, [formData.paymentTypeId, paymentTypes, paymentFrequencies]);

  // Set default location and position for new records (always set defaults, not just for active)
  useEffect(() => {
    // Set defaults when opening modal for new record (when editingRecord is null and form is reset)
    if (!editingRecord && (!formData.locationId || formData.locationId === 0) && employee.profile?.mainLocationId) {
      setFormData(prev => ({ ...prev, locationId: employee.profile!.mainLocationId! }));
    }
    if (!editingRecord && (!formData.positionId || formData.positionId === 0) && employee.profile?.mainPositionId) {
      setFormData(prev => ({ ...prev, positionId: employee.profile!.mainPositionId! }));
    }
  }, [editingRecord, employee.profile]);

  // Set default OT values from company settings when overtime is enabled
  useEffect(() => {
    if (formData.overtimeEligible && companySettings) {
      // Only set defaults if values are not already set (for new records or when enabling overtime)
      const updates: Partial<CreatePayRecordRequest> = {};
      
      if (!formData.ot1TriggerHours && companySettings.otRate1) {
        updates.ot1TriggerHours = companySettings.otRate1;
      }
      if (!formData.ot2TriggerHours && companySettings.otRate2) {
        updates.ot2TriggerHours = companySettings.otRate2;
      }
      if (!formData.ot1RateMultiplier && companySettings.ot1RateMultiplier) {
        updates.ot1RateMultiplier = companySettings.ot1RateMultiplier;
      }
      if (!formData.ot2RateMultiplier && companySettings.ot2RateMultiplier) {
        updates.ot2RateMultiplier = companySettings.ot2RateMultiplier;
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [formData.overtimeEligible, companySettings, formData.ot1TriggerHours, formData.ot2TriggerHours, formData.ot1RateMultiplier, formData.ot2RateMultiplier]);

  const payRecordScheduleIds = useMemo(
    () => Array.from(new Set(payRecords.map((r) => r.scheduleId).filter((id) => id > 0))),
    [payRecords]
  );

  const scheduleIdsForResolvedRates = useMemo(() => {
    const ids = new Set(payRecordScheduleIds);
    if (showPayRecordModal && formData.scheduleId > 0) {
      ids.add(formData.scheduleId);
    }
    return Array.from(ids);
  }, [payRecordScheduleIds, showPayRecordModal, formData.scheduleId]);

  const asOfForResolvedRates =
    showPayRecordModal && formData.effectiveFrom
      ? formData.effectiveFrom.slice(0, 10)
      : undefined;

  useEffect(() => {
    if (!healthcareNurseBandingPayrollEnabled || !employee.id) {
      setHasNurseBandHistory(false);
      setNurseBandingResolvedRates(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const history = await nurseBandingApi.getBandYearHistory(employee.id);
        if (cancelled) return;
        const hasHistory = (history?.length ?? 0) > 0;
        setHasNurseBandHistory(hasHistory);
        if (!hasHistory) {
          setNurseBandingResolvedRates(null);
          return;
        }
        const resolved = await nurseBandingApi.getResolvedRates(employee.id, {
          asOf: asOfForResolvedRates,
          extraScheduleIds: scheduleIdsForResolvedRates,
        });
        if (!cancelled) {
          setNurseBandingResolvedRates(resolved);
        }
      } catch {
        if (!cancelled) {
          setHasNurseBandHistory(false);
          setNurseBandingResolvedRates(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [employee.id, healthcareNurseBandingPayrollEnabled, scheduleIdsForResolvedRates, asOfForResolvedRates]);

  const showValidationDialog = (message: string) => {
    setValidationDialogMessage(message);
    setSubmitError(message);
  };

  const validatePayRecordForm = (): string | null => {
    if (!formData.locationId || formData.locationId === 0) {
      return 'Location is required.';
    }
    if (!formData.positionId || formData.positionId === 0) {
      return 'Position is required.';
    }
    if (!formData.scheduleId || formData.scheduleId === 0) {
      return 'Main Schedule is required.';
    }
    if (!formData.contractedHours || formData.contractedHours === 0) {
      return 'Contracted Hours is required.';
    }

    const payType = paymentTypes.find((pt) => pt.id === formData.paymentTypeId);
    const isProjectType = payType?.name === 'Project';
    if (!isProjectType && !nurseBandingPay && (!formData.baseRate || formData.baseRate === 0)) {
      return 'Pay amount is required.';
    }

    if (!nurseBandingPay && formData.overtimeEligible) {
      if (!formData.ot1TriggerHours || formData.ot1TriggerHours === 0) {
        return 'OT1 Trigger Hours is required when Overtime Eligible is checked.';
      }
      if (!formData.ot1RateMultiplier || formData.ot1RateMultiplier === 0) {
        return 'OT1 Rate Multiplier is required when Overtime Eligible is checked.';
      }
      if (!formData.ot2TriggerHours || formData.ot2TriggerHours === 0) {
        return 'OT2 Trigger Hours is required when Overtime Eligible is checked.';
      }
      if (!formData.ot2RateMultiplier || formData.ot2RateMultiplier === 0) {
        return 'OT2 Rate Multiplier is required when Overtime Eligible is checked.';
      }
    }

    const paymentType = paymentTypes.find((pt) => pt.id === formData.paymentTypeId);
    if (paymentType?.name === 'Project') {
      if (!formData.paymentFrequencyId) {
        return 'Pay Frequency is required when Pay Type is "Project".';
      }
      if (!formData.projectStartDate) {
        return 'Project Start Date is required when Pay Type is "Project".';
      }
      if (!formData.projectEndDate) {
        return 'Project End Date is required when Pay Type is "Project".';
      }
      if (formData.projectStartDate && formData.projectEndDate) {
        const startDate = new Date(formData.projectStartDate);
        const endDate = new Date(formData.projectEndDate);
        if (endDate <= startDate) {
          return 'Project End Date must be after Project Start Date.';
        }
      }
    }

    if (!nurseBandingPay) {
      return validatePayRateMatrices(payRateMatrices);
    }

    return null;
  };

  const handlePayRecordSubmit = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (saving) return;

    setSubmitError(null);
    setValidationDialogMessage(null);

    const validationError = validatePayRecordForm();
    if (validationError) {
      showValidationDialog(validationError);
      return;
    }
    
    try {
      setSaving(true);
      
      const orderedPayRateMatrices = nurseBandingPay ? [] : renumberPayRateMatrices(payRateMatrices);
      const normalizedPayRateMatrices = orderedPayRateMatrices.map(({ id, displayOrder, locationId, positionId, scheduleId, payRate }) => {
        const row: CreatePayRateMatrixRequest = {
          displayOrder,
          locationId,
          positionId,
          scheduleId,
          payRate: payRate ?? 0,
        };
        if (id != null && id > 0) {
          row.id = id;
        }
        return row;
      });
      const payRecordPayload = nurseBandingPay
        ? {
            ...formData,
            baseRate: 0,
            sundayRateMultiplier: undefined,
            bankHolidayRateMultiplier: undefined,
            nightShiftMultiplier: undefined,
            overtimeEligible: false,
            ot1TriggerHours: undefined,
            ot2TriggerHours: undefined,
            ot1RateMultiplier: undefined,
            ot2RateMultiplier: undefined,
          }
        : formData;

      // For active records, copy EffectiveFrom to FromDate when saving
      if (editingRecord) {
        const updateData: UpdatePayRecordRequest = {
          ...payRecordPayload,
          workingDayIds: selectedWorkingDayIds.length > 0 ? selectedWorkingDayIds : undefined,
          payRateMatrices: normalizedPayRateMatrices.length > 0 ? normalizedPayRateMatrices : [],
        };

        if (formData.isActive && formData.effectiveFrom) {
          updateData.fromDate = formData.effectiveFrom;
        }

        await payRecordApi.update(editingRecord.id, updateData);
        toast.success('Pay record updated successfully!');
      } else {
        const createData: CreatePayRecordRequest = {
          ...payRecordPayload,
          workingDayIds: selectedWorkingDayIds.length > 0 ? selectedWorkingDayIds : undefined,
          payRateMatrices: normalizedPayRateMatrices.length > 0 ? normalizedPayRateMatrices : undefined,
        };

        if (formData.isActive && formData.effectiveFrom) {
          createData.fromDate = formData.effectiveFrom;
        }

        await payRecordApi.create(createData);
        toast.success('Pay record created successfully!');
      }
      
      setShowPayRecordModal(false);
      setEditingRecord(null);
      resetForm();
      await onRefresh();
    } catch (error: any) {
      console.error('Error saving pay record:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save pay record. Please try again.';
      showValidationDialog(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: employee.id,
      locationId: employee.profile?.mainLocationId || 0,
      positionId: employee.profile?.mainPositionId || 0,
      scheduleId: 0,
      paymentTypeId: 1,
      contractedHours: 0,
      baseRate: 0,
      overtimeEligible: false,
      ot1TriggerHours: undefined,
      ot2TriggerHours: undefined,
      ot1RateMultiplier: undefined,
      ot2RateMultiplier: undefined,
      isActive: false,
      projectStartDate: undefined,
      projectEndDate: undefined,
    });
    setSelectedWorkingDayIds([]);
    setPayRateMatrices([]);
  };

  const handleEdit = (record: PayRecord) => {
    setEditingRecord(record);
    setSubmitError(null);
    setValidationDialogMessage(null);
    // Active rows: API/list may have the start date only on FromDate (EffectiveFrom null). Match table fallback.
    const effectiveFromRaw = record.effectiveFrom ?? (record.isActive ? record.fromDate : undefined);
    const effectiveFromNormalized = effectiveFromRaw ? String(effectiveFromRaw).slice(0, 10) : undefined;
    setFormData({
      employeeId: employee.id,
      fromDate: record.fromDate ? record.fromDate.split('T')[0] : undefined,
      toDate: record.toDate ? record.toDate.split('T')[0] : undefined,
      locationId: record.locationId ?? employee.profile?.mainLocationId ?? 0,
      scheduleId: record.scheduleId,
      positionId: record.positionId ?? employee.profile?.mainPositionId ?? 0,
      effectiveFrom: effectiveFromNormalized,
      isActive: record.isActive,
      paymentTypeId: record.paymentTypeId,
      paymentFrequencyId: record.paymentFrequencyId ?? undefined,
      contractedHours: record.contractedHours ?? 0,
      baseRate: record.baseRate ?? 0,
      sundayRateMultiplier: record.sundayRateMultiplier ?? undefined,
      bankHolidayRateMultiplier: record.bankHolidayRateMultiplier ?? undefined,
      nightShiftMultiplier: record.nightShiftMultiplier ?? undefined,
      overtimeEligible: record.overtimeEligible,
      ot1TriggerHours: record.ot1TriggerHours ?? undefined,
      ot2TriggerHours: record.ot2TriggerHours ?? undefined,
      ot1RateMultiplier: record.ot1RateMultiplier ?? undefined,
      ot2RateMultiplier: record.ot2RateMultiplier ?? undefined,
      assignLaborCostById: record.assignLaborCostById ?? undefined,
      workingDaysPerWeek: record.workingDaysPerWeek ?? undefined,
      dailyRate: record.dailyRate ?? undefined,
      // Project-specific fields
      projectStartDate: record.projectStartDate ? record.projectStartDate.split('T')[0] : undefined,
      projectEndDate: record.projectEndDate ? record.projectEndDate.split('T')[0] : undefined,
    });
    
    const workingDayIds = record.payRecordWorkingDays?.map(prwd => prwd.workingDayId) || [];
    setSelectedWorkingDayIds(workingDayIds);
    
    const matrices = nurseBandingPay
      ? []
      : (record.payRateMatrices || [])
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id)
          .map((prm, index) => ({
            id: prm.id,
            displayOrder: index,
            locationId: prm.locationId ?? undefined,
            positionId: prm.positionId ?? undefined,
            scheduleId: prm.scheduleId ?? undefined,
            payRate: prm.payRate,
          }));
    setPayRateMatrices(matrices);
    
    setShowPayRecordModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this pay record?')) {
      try {
        await payRecordApi.delete(id);
        await onRefresh();
        toast.success('Pay record deleted successfully!');
      } catch (error) {
        console.error('Error deleting pay record:', error);
        toast.error('Failed to delete pay record. Please try again.');
      }
    }
  };

  const addPayRateMatrixRow = () => {
    setSubmitError(null);
    setValidationDialogMessage(null);
    setPayRateMatrices(renumberPayRateMatrices([
      ...payRateMatrices,
      {
        displayOrder: payRateMatrices.length,
        positionId: undefined,
        scheduleId: undefined,
        payRate: undefined,
      },
    ]));
  };

  const removePayRateMatrixRow = (index: number) => {
    setPayRateMatrices(renumberPayRateMatrices(payRateMatrices.filter((_, i) => i !== index)));
  };

  const movePayRateMatrixRow = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === payRateMatrices.length - 1) return;
    
    const updated = [...payRateMatrices];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setPayRateMatrices(renumberPayRateMatrices(updated));
  };

  const updatePayRateMatrixRow = (index: number, field: keyof CreatePayRateMatrixRequest, value: any) => {
    const updated = [...payRateMatrices];
    updated[index] = { ...updated[index], [field]: value };
    setPayRateMatrices(updated);
  };

  // Show weekly/monthly fields for Salary, Daily, and Project (not for Hourly)
  const paymentTypeName = formData.paymentTypeId
    ? (paymentTypes.find(pt => pt.id === formData.paymentTypeId)?.name || '')
    : '';
  const isWeeklyOrMonthly = formData.paymentTypeId ?
    !['Hourly'].includes(paymentTypeName) : false;
  const assignLaborCostBy = assignLaborCostByOptions.find(alcbo => alcbo.id === formData.assignLaborCostById);
  const showWorkingDays = assignLaborCostBy?.name === 'Pre Set Days';
  // Working days/week drives leave & BH day hours (CH ÷ WDPW) for all payment types including nurses
  const showWorkingDaysPerWeek = true;
  const showDailyRate = paymentTypeName === 'Daily' || assignLaborCostBy?.name === 'Daily Rate';
  // Show project-specific fields only when Payment Type is "Project"
  const isProject = paymentTypeName === 'Project';

  const payrollDisplay = payrollForm ?? {
    worksNumber: employee.payroll?.worksNumber ?? '',
    ppsNumber: employee.payroll?.ppsNumber ?? '',
    ban: employee.payroll?.ban ?? '',
    bic: employee.payroll?.bic ?? '',
    accountNumber: employee.payroll?.accountNumber ?? '',
    sortCode: employee.payroll?.sortCode ?? '',
    accountName: employee.payroll?.accountName ?? '',
    bankName: employee.payroll?.bankName ?? '',
    isPremiumPayEligible: employee.payroll?.isPremiumPayEligible ?? true,
    allowAdditiveHourlyPay: employee.payroll?.allowAdditiveHourlyPay ?? false,
  };
  const payrollEditable = Boolean(payrollForm && onPayrollFieldChange);
  // Filter payment frequencies based on payment type
  // For Project: show only "Contract" and "Monthly Invoice"
  // For others: show regular frequencies (Weekly, Monthly, Fortnightly)
  const availablePaymentFrequencies = isProject
    ? paymentFrequencies.filter(pf => pf.name === 'Contract' || pf.name === 'Monthly Invoice')
    : paymentFrequencies.filter(pf => pf.name !== 'Contract' && pf.name !== 'Monthly Invoice');

  return (
    <div className="space-y-6">
      {/* Payroll Section */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">Payroll Information</h2>
          {payrollEditable && (
            <p className="text-sm text-neutral-500 mt-1">
              Use <span className="font-medium text-neutral-700">Save Changes</span> at the bottom of the page to persist updates.
            </p>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Works Number</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.worksNumber ?? ''}
                onChange={(e) => onPayrollFieldChange('worksNumber', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.worksNumber || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">PPS Number</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.ppsNumber ?? ''}
                onChange={(e) => onPayrollFieldChange('ppsNumber', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.ppsNumber || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Account Number</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.accountNumber ?? ''}
                onChange={(e) => onPayrollFieldChange('accountNumber', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.accountNumber || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Account Name</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.accountName ?? ''}
                onChange={(e) => onPayrollFieldChange('accountName', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.accountName || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Name</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.bankName ?? ''}
                onChange={(e) => onPayrollFieldChange('bankName', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.bankName || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">BAN</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.ban ?? ''}
                onChange={(e) => onPayrollFieldChange('ban', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.ban || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">BIC</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.bic ?? ''}
                onChange={(e) => onPayrollFieldChange('bic', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.bic || 'N/A'}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Sort Code</label>
            {payrollEditable && onPayrollFieldChange ? (
              <input
                type="text"
                value={payrollDisplay.sortCode ?? ''}
                onChange={(e) => onPayrollFieldChange('sortCode', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
                {employee.payroll?.sortCode || 'N/A'}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            {!nurseBandingPay && (
            <>
            <label className="flex items-center gap-2 cursor-pointer">
              {payrollEditable && onPayrollFieldChange ? (
                <input
                  type="checkbox"
                  checked={payrollDisplay.isPremiumPayEligible ?? true}
                  onChange={(e) => onPayrollFieldChange('isPremiumPayEligible', e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="checkbox"
                  checked={employee.payroll?.isPremiumPayEligible ?? true}
                  disabled
                  className="h-4 w-4 rounded border-neutral-300"
                />
              )}
              <span className="text-sm font-medium text-neutral-700">
                Eligible for premium pay (Sunday &amp; bank holiday)
              </span>
            </label>
            <p className="text-xs text-neutral-500 mt-1 ml-6">
              Legacy AOS &quot;Is Eligible For Premium Pay?&quot; — when off, Sunday and bank holiday premiums are not paid.
            </p>
            </>
            )}
            <label className="flex items-center gap-2 cursor-pointer mt-3">
              {payrollEditable && onPayrollFieldChange ? (
                <input
                  type="checkbox"
                  checked={payrollDisplay.allowAdditiveHourlyPay ?? false}
                  onChange={(e) => onPayrollFieldChange('allowAdditiveHourlyPay', e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="checkbox"
                  checked={employee.payroll?.allowAdditiveHourlyPay ?? false}
                  disabled
                  className="h-4 w-4 rounded border-neutral-300"
                />
              )}
              <span className="text-sm font-medium text-neutral-700">
                Allow additive hourly / multi-role (Pay Extra)
              </span>
            </label>
            <p className="text-xs text-neutral-500 mt-1 ml-6">
              Salaried staff who flex into a second role: full salary plus matrix pay for hours tagged Pay Extra on shifts/timecards.
            </p>
          </div>
        </div>
      </div>

      {/* Pay Records Section */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">Pay Records</h2>
          <button
            type="button"
            onClick={() => {
              setEditingRecord(null);
              setSubmitError(null);
              setValidationDialogMessage(null);
              resetForm();
              setShowPayRecordModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Pay Record
          </button>
        </div>

        {payRecords.length === 0 ? (
          <p className="text-neutral-500">No pay records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {payRecords.some(pr => pr.isActive) ? 'Effective From' : 'From Date'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {payRecords.some(pr => !pr.isActive) ? 'To Date' : ''}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Payment Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Pay amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {payRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${record.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {record.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {record.isActive && record.effectiveFrom 
                        ? formatCompanyDate(record.effectiveFrom)
                        : record.fromDate 
                        ? formatCompanyDate(record.fromDate)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {!record.isActive && record.toDate ? formatCompanyDate(record.toDate) : record.isActive ? '' : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.location?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.position?.title || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.paymentType?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {formatPayListAmount(record, nurseBandingResolvedRates)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => handleEdit(record)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit className="h-4 w-4 inline" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(record.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {healthcareNurseBandingPayrollEnabled && (
        <NurseBandYearHistoryPanel
          employeeId={employee.id}
          positions={employeePositions}
          onSaved={async () => {
            if (!employee.id) return;
            try {
              const resolved = await nurseBandingApi.getResolvedRates(employee.id, {
                asOf: asOfForResolvedRates,
                extraScheduleIds: scheduleIdsForResolvedRates,
              });
              setNurseBandingResolvedRates(resolved);
            } catch {
              /* ignore */
            }
          }}
        />
      )}

      {/* Pay Record Modal - portaled to avoid nested form inside employee profile save form */}
      {showPayRecordModal && createPortal(
        <PayRecordModal
          formData={formData}
          setFormData={setFormData}
          selectedWorkingDayIds={selectedWorkingDayIds}
          setSelectedWorkingDayIds={setSelectedWorkingDayIds}
          payRateMatrices={payRateMatrices}
          setPayRateMatrices={setPayRateMatrices}
          addPayRateMatrixRow={addPayRateMatrixRow}
          removePayRateMatrixRow={removePayRateMatrixRow}
          movePayRateMatrixRow={movePayRateMatrixRow}
          updatePayRateMatrixRow={updatePayRateMatrixRow}
          editingRecord={editingRecord}
          saving={saving}
          onClose={() => {
            setShowPayRecordModal(false);
            setEditingRecord(null);
            setSubmitError(null);
            setValidationDialogMessage(null);
            resetForm();
          }}
          onSubmit={handlePayRecordSubmit}
          submitError={submitError}
          locations={employeeLocations}
          positions={employeePositions}
          schedules={payRecordModalSchedules}
          paymentTypes={paymentTypes}
          paymentFrequencies={paymentFrequencies}
          assignLaborCostByOptions={assignLaborCostByOptions}
          workingDays={workingDays}
          isWeeklyOrMonthly={isWeeklyOrMonthly}
          showWorkingDays={showWorkingDays}
          showWorkingDaysPerWeek={showWorkingDaysPerWeek}
          showDailyRate={showDailyRate}
          nurseBandingPay={nurseBandingPay}
          nurseBandingResolvedRates={nurseBandingResolvedRates}
        />,
        document.body
      )}

      {validationDialogMessage && createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">Cannot save pay record</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{validationDialogMessage}</p>
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                type="button"
                onClick={() => setValidationDialogMessage(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Pay Record Modal Component (separated for readability)
interface PayRecordModalProps {
  formData: CreatePayRecordRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreatePayRecordRequest>>;
  selectedWorkingDayIds: number[];
  setSelectedWorkingDayIds: React.Dispatch<React.SetStateAction<number[]>>;
  payRateMatrices: CreatePayRateMatrixRequest[];
  setPayRateMatrices: React.Dispatch<React.SetStateAction<CreatePayRateMatrixRequest[]>>;
  addPayRateMatrixRow: () => void;
  removePayRateMatrixRow: (index: number) => void;
  movePayRateMatrixRow: (index: number, direction: 'up' | 'down') => void;
  updatePayRateMatrixRow: (index: number, field: keyof CreatePayRateMatrixRequest, value: any) => void;
  editingRecord: PayRecord | null;
  saving: boolean;
  submitError: string | null;
  onClose: () => void;
  onSubmit: (e?: React.SyntheticEvent) => void;
  locations: Location[];
  positions: Position[];
  schedules: Schedule[];
  paymentTypes: PaymentType[];
  paymentFrequencies: PaymentFrequency[];
  assignLaborCostByOptions: AssignLaborCostByOption[];
  workingDays: WorkingDay[];
  isWeeklyOrMonthly: boolean;
  showWorkingDays: boolean;
  showWorkingDaysPerWeek: boolean;
  showDailyRate?: boolean;
  nurseBandingPay?: boolean;
  nurseBandingResolvedRates?: NurseBandResolvedRates | null;
}

const PayRecordModal: React.FC<PayRecordModalProps> = ({
  formData,
  setFormData,
  selectedWorkingDayIds,
  setSelectedWorkingDayIds,
  payRateMatrices,
  addPayRateMatrixRow,
  removePayRateMatrixRow,
  movePayRateMatrixRow,
  updatePayRateMatrixRow,
  editingRecord,
  saving,
  submitError,
  onClose,
  onSubmit,
  locations,
  positions,
  schedules,
  paymentTypes,
  paymentFrequencies,
  assignLaborCostByOptions,
  workingDays,
  isWeeklyOrMonthly,
  showWorkingDays,
  showWorkingDaysPerWeek,
  showDailyRate = false,
  nurseBandingPay = false,
  nurseBandingResolvedRates = null,
}) => {
  // Show project-specific fields only when Payment Type is "Project"
  const isProject = formData.paymentTypeId ? 
    paymentTypes.find(pt => pt.id === formData.paymentTypeId)?.name === 'Project' : false;
  // Filter payment frequencies based on payment type
  // For Project: show only "Contract" and "Monthly Invoice"
  // For others: show regular frequencies (Weekly, Monthly, Fortnightly)
  const availablePaymentFrequencies = isProject
    ? paymentFrequencies.filter(pf => pf.name === 'Contract' || pf.name === 'Monthly Invoice')
    : paymentFrequencies.filter(pf => pf.name !== 'Contract' && pf.name !== 'Monthly Invoice');
  const paymentTypeName = paymentTypes.find((pt) => pt.id === formData.paymentTypeId)?.name;
  const paymentFrequencyName = paymentFrequencies.find((pf) => pf.id === formData.paymentFrequencyId)?.name;
  const payRateBaseField = getPayRateBaseFieldLabel(paymentTypeName, paymentFrequencyName);
  const payRateRequired = !isProject && !nurseBandingPay;
  const bandingPositionIds = positions.map((p) => p.id).filter((id) => id > 0);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-neutral-800">{editingRecord ? 'Edit Pay Record' : 'Add Pay Record'}</h3>
              <p className="text-sm text-neutral-500 mt-1">Configure employee pay record details</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors p-1 rounded-full hover:bg-neutral-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="px-6 py-6">
        {submitError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {submitError}
          </div>
        )}
        
        <div className="space-y-6">
          {/* Section 1: Record Status & Dates */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Record Status & Dates</h4>
              <p className="text-xs text-neutral-500 mt-1">Set the record status and effective date range</p>
            </div>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive || false}
                  onChange={(e) => {
                    const isActive = e.target.checked;
                    setFormData({
                      ...formData,
                      isActive,
                      // When activating, copy EffectiveFrom to FromDate if it exists
                      fromDate: isActive && formData.effectiveFrom ? formData.effectiveFrom : formData.fromDate,
                      // When activating, clear ToDate
                      toDate: isActive ? undefined : formData.toDate,
                    });
                  }}
                  disabled={saving}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-neutral-700">Active Record (Latest record should be active)</span>
              </label>
            </div>

            {/* Date Fields - Conditional based on IsActive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.isActive ? (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Effective From *</label>
                  <input
                    type="date"
                    required={formData.isActive}
                    value={formData.effectiveFrom || ''}
                    onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value, fromDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">From Date *</label>
                    <input
                      type="date"
                      required={!formData.isActive}
                      value={formData.fromDate || ''}
                      onChange={(e) => setFormData({ ...formData, fromDate: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={formData.toDate || ''}
                      onChange={(e) => setFormData({ ...formData, toDate: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 2: Assignment Details */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Assignment Details</h4>
              <p className="text-xs text-neutral-500 mt-1">Select location, position, and schedule for this pay record</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Location *</label>
              <select
                required
                value={formData.locationId || ''}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value ? parseInt(e.target.value) : 0 })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Position *</label>
              <select
                required
                value={formData.positionId || ''}
                onChange={(e) => setFormData({ ...formData, positionId: e.target.value ? parseInt(e.target.value) : 0 })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Position</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.id}>{pos.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Main Schedule *</label>
              <select
                required
                value={formData.scheduleId}
                onChange={(e) => setFormData({ ...formData, scheduleId: parseInt(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Schedule</option>
                {schedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
                ))}
              </select>
            </div>
            </div>
          </div>

          {/* Section 3: Payment Configuration */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Payment Configuration</h4>
              <p className="text-xs text-neutral-500 mt-1">
                {nurseBandingPay
                  ? 'Payment type and contracted hours — hourly rates come from nurse banding rate cards'
                  : 'Configure payment type, frequency, hours, and pay amount'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Pay Type *</label>
              <select
                required
                value={formData.paymentTypeId}
                onChange={(e) => setFormData({ ...formData, paymentTypeId: parseInt(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {paymentTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Pay Frequency{isProject && ' *'}
              </label>
              <select
                required={isProject}
                value={formData.paymentFrequencyId || ''}
                onChange={(e) => setFormData({ ...formData, paymentFrequencyId: e.target.value ? parseInt(e.target.value) : undefined })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Select Payment Frequency</option>
                {availablePaymentFrequencies.map((pf) => (
                  <option key={pf.id} value={pf.id}>{pf.name}</option>
                ))}
              </select>
            </div>
            </div>
            
            <div className={`grid grid-cols-1 gap-4 ${nurseBandingPay ? 'md:grid-cols-2' : 'md:grid-cols-2'}`}>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Contracted Hours (per week) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0.01"
                  value={formData.contractedHours || ''}
                  onChange={(e) => setFormData({ ...formData, contractedHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
              {showWorkingDaysPerWeek && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Working days per week</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="7"
                    value={formData.workingDaysPerWeek ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        workingDaysPerWeek: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    disabled={saving}
                    placeholder="Default 5"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Used for leave/BH day hours (contracted hours ÷ working days). Default 5 if blank.
                  </p>
                </div>
              )}
              {!nurseBandingPay && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {payRateBaseField.label}
                  {payRateRequired ? ' *' : ''}
                </label>
                <input
                  type="number"
                  step="0.01"
                  required={payRateRequired}
                  min="0"
                  value={formData.baseRate || ''}
                  onChange={(e) => setFormData({ ...formData, baseRate: e.target.value ? parseFloat(e.target.value) : 0 })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  aria-label={payRateBaseField.label}
                />
              </div>
              )}
              {showDailyRate && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Daily rate (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.dailyRate ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        dailyRate: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              )}
            </div>

            {nurseBandingPay && (
              <div className="rounded-md border border-blue-200 bg-white px-4 py-3 text-sm">
                <p className="font-medium text-neutral-800 mb-1">Position banding rates (read-only)</p>
                <p className="text-xs text-neutral-600 mb-2">
                  Rates for main and sub positions at this pay record&apos;s effective band year.
                </p>
                <NurseBandingResolvedRatesPanel
                  rates={nurseBandingResolvedRates}
                  filterPositionIds={bandingPositionIds}
                />
              </div>
            )}
          </div>

          {!nurseBandingPay && (
          <>
          {/* Section 4: Rate Multipliers */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Rate Multipliers</h4>
              <p className="text-xs text-neutral-500 mt-1">Configure multipliers for special rates (Sunday, Bank Holiday, Night Shift)</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sunday Rate Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.sundayRateMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, sundayRateMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 1.5"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Holiday Rate Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.bankHolidayRateMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, bankHolidayRateMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 2.0"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Night Shift Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.nightShiftMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, nightShiftMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 1.5"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            </div>
          </div>

          {/* Section 5: Overtime Configuration */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Overtime Configuration</h4>
              <p className="text-xs text-neutral-500 mt-1">Configure overtime eligibility and OT1/OT2 rates</p>
            </div>
            
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.overtimeEligible || false}
                  onChange={(e) => setFormData({ ...formData, overtimeEligible: e.target.checked })}
                  disabled={saving}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-neutral-700">Overtime Eligible</span>
              </label>
              {formData.overtimeEligible && (
                <div className="bg-white rounded-lg p-4 space-y-4 border border-blue-200">
                  <div>
                    <h5 className="text-sm font-semibold text-neutral-700 mb-3">OT1 Settings</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT1 Trigger Hours (per week) *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot1TriggerHours || ''}
                          onChange={(e) => setFormData({ ...formData, ot1TriggerHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT1 Rate Multiplier *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot1RateMultiplier || ''}
                          onChange={(e) => setFormData({ ...formData, ot1RateMultiplier: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          placeholder="e.g., 1.5"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-semibold text-neutral-700 mb-3">OT2 Settings</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT2 Trigger Hours (per week) *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot2TriggerHours || ''}
                          onChange={(e) => setFormData({ ...formData, ot2TriggerHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT2 Rate Multiplier *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot2RateMultiplier || ''}
                          onChange={(e) => setFormData({ ...formData, ot2RateMultiplier: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          placeholder="e.g., 2.0"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 6: Salary/Daily/Project Specific Fields */}
          {isWeeklyOrMonthly && (
            <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
              <div className="border-b border-blue-200 pb-2">
                <h4 className="text-base font-semibold text-neutral-800">Salary/Daily/Project Payment Fields</h4>
                <p className="text-xs text-neutral-500 mt-1">Configure labor cost assignment and working days</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Assign Labor Cost By</label>
                <select
                  value={formData.assignLaborCostById || ''}
                  onChange={(e) => setFormData({ ...formData, assignLaborCostById: e.target.value ? parseInt(e.target.value) : undefined })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Select Option</option>
                  {assignLaborCostByOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>

              {showWorkingDays && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Working Days</label>
                  <div className="grid grid-cols-2 gap-2">
                    {workingDays.map((day) => (
                      <label key={day.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedWorkingDayIds.includes(day.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedWorkingDayIds([...selectedWorkingDayIds, day.id]);
                            } else {
                              setSelectedWorkingDayIds(selectedWorkingDayIds.filter(id => id !== day.id));
                            }
                          }}
                          disabled={saving}
                          className="mr-2"
                        />
                        <span className="text-sm text-neutral-700">{day.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 7: Project Specific Fields */}
          {isProject && (
            <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
              <div className="border-b border-blue-200 pb-2">
                <h4 className="text-base font-semibold text-neutral-800">Project Payment Fields</h4>
                <p className="text-xs text-neutral-500 mt-1">Set project start and end dates</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Project Start Date *</label>
                  <input
                    type="date"
                    required={isProject}
                    value={formData.projectStartDate || ''}
                    onChange={(e) => setFormData({ ...formData, projectStartDate: e.target.value || undefined })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Project End Date *</label>
                  <input
                    type="date"
                    required={isProject}
                    value={formData.projectEndDate || ''}
                    onChange={(e) => setFormData({ ...formData, projectEndDate: e.target.value || undefined })}
                    disabled={saving}
                    min={formData.projectStartDate || undefined}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Section 8: Pay Rate Override Matrix */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-base font-semibold text-neutral-800">Pay Rate Override Matrix</h4>
                  <p className="text-xs text-neutral-500 mt-1">Configure position/schedule-specific pay rate overrides</p>
                </div>
                <button
                  type="button"
                  onClick={addPayRateMatrixRow}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </button>
              </div>
              <p className="text-xs text-neutral-500 italic mt-2">
                Leave Position or Schedule blank to use as a wildcard (applies to all). At least one of these fields must have a value.
              </p>
            </div>
            
            {payRateMatrices.length === 0 ? (
              <p className="text-sm text-neutral-500">No pay rate override matrix rows. Click "Add Row" to add one.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Order</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Position</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Schedule</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Pay Rate (Hourly) *</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {payRateMatrices.map((row, index) => (
                      <tr key={row.id != null && row.id > 0 ? `matrix-${row.id}` : `matrix-new-${index}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => movePayRateMatrixRow(index, 'up')}
                              disabled={index === 0}
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => movePayRateMatrixRow(index, 'down')}
                              disabled={index === payRateMatrices.length - 1}
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            value={row.positionId || ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'positionId', e.target.value ? parseInt(e.target.value) : undefined)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <option value="">All Positions</option>
                            {positions.map((pos) => (
                              <option key={pos.id} value={pos.id}>{pos.title}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            value={row.scheduleId || ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'scheduleId', e.target.value ? parseInt(e.target.value) : undefined)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <option value="">All Schedules</option>
                            {schedules.map((schedule) => (
                              <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={row.payRate ?? ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'payRate', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                            disabled={saving}
                            placeholder="0.00"
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removePayRateMatrixRow(index)}
                            disabled={saving}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
          )}

          {/* Form Actions */}
          <div className="sticky bottom-0 bg-white border-t border-neutral-200 px-6 py-4 -mx-6 -mb-6 mt-8">
            <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={onSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
            </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollAndPayRecords;
